import { memo, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Box, DialogActions, IconButton, InputAdornment } from "@mui/material";

import { ReactComponent as IconEyeOff } from "assets/svg/20-eye-off.svg";
import { ReactComponent as IconEye } from "assets/svg/20-eye.svg";
import { ReactComponent as IconLock } from "assets/svg/20-lock.svg";

import { FormInput } from "components/common/Form";
import pxToRem from "theme/base/pxToRem";

import {
  ButtonCustom,
  QRWrapper,
  ResetQRImage,
  QRNote,
} from "../Modal2FA/style";
import { accountInfoDataSelector } from "layouts/Sidenav/redux/selector";
import { resetQrSelector } from "modules/Auth/redux/selector";
import { resetDataQr, resetQrStart } from "modules/Auth/redux/resetQr";

import { SixDigitOtpFields } from "./TabHasApp";
import { useKeyCap } from "helper/customHook";

const FIELD = "password";

type FormShape = Record<typeof FIELD, string>;

const emptyForm = (): FormShape => ({ password: "" });

export type TabNoAppProps = {
  onSubmitOtp: (otp: string) => void | Promise<void>;
  submitting: boolean;
  resetBusy: boolean;
};

const TabNoApp = memo(function TabNoApp({
  onSubmitOtp,
  submitting,
  resetBusy,
}: TabNoAppProps) {
  const { onEnter } = useKeyCap();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState<FormShape>(emptyForm);
  const [showPassword, setShowPassword] = useState(false);

  const { masterAccount } = useSelector(accountInfoDataSelector);
  const qrReset = useSelector(resetQrSelector);

  const qrResetUrl = qrReset ? URL.createObjectURL(qrReset) : null;

  useEffect(() => {
    if (!qrResetUrl) return;
    return () => {
      URL.revokeObjectURL(qrResetUrl);
    };
  }, [qrResetUrl]);

  useEffect(() => {
    return () => {
      dispatch(resetDataQr());
    };
  }, [dispatch]);

  const onPasswordChange = useCallback(
    (_name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData((prev) => ({ ...prev, [FIELD]: value }));
    },
    []
  );

  const handleClickShowPassword = useCallback(() => {
    setShowPassword((s) => !s);
  }, []);

  const handleMouseDownPassword = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    },
    []
  );

  const handleSubmitNoApp = useCallback(() => {
    const password = formData.password?.trim();
    const username = masterAccount?.trim();
    if (!password) {
      toast.error("Vui lòng nhập mật khẩu");
      return;
    }
    if (!username) {
      toast.error("Không tìm thấy tài khoản master");
      return;
    }
    dispatch(resetQrStart({ username, password }));
  }, [dispatch, formData.password, masterAccount]);

  return (
    <>
      <Box
        sx={{
          py: 1,
          color: "text.secondary",
          width: 500,
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          gap: pxToRem(16),
        }}
      >
        {!qrResetUrl && (
          <>
            <Box
              component="p"
              sx={{ m: 0, typography: "body2", textAlign: "center" }}
            >
              Vui lòng nhập mật khẩu tài khoản để kích hoạt ứng dụng xác thực
            </Box>
            <FormInput
              name={FIELD}
              formData={formData}
              formErrors={{}}
              placeholder="Nhập mật khẩu của bạn"
              label="Mật khẩu"
              labelIcon={<IconLock />}
              onChange={onPasswordChange}
              inputProps={{
                className: "password",
                type: showPassword ? "text" : "password",
                sx: { padding: pxToRem(16) },
                endAdornment: (
                  <InputAdornment position="end" style={{ zIndex: 1001 }}>
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <IconEye /> : <IconEyeOff />}
                    </IconButton>
                  </InputAdornment>
                ),
                onKeyDown: (e) => {
                  onEnter(handleSubmitNoApp)(e);
                },
              }}
              fontWeight={400}
            />
          </>
        )}

        {qrResetUrl && (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap={pxToRem(12)}
          >
            <QRWrapper>
              <ResetQRImage src={qrResetUrl} alt="QR code" />
            </QRWrapper>

            <QRNote
              sx={{
                color: (theme) => theme.palette.colorsBase.gray_6,
              }}
            >
              Quét mã QR bằng Google Authenticator, rồi nhập mã 6 số bên dưới
            </QRNote>

            <SixDigitOtpFields
              submitting={submitting}
              onSubmitOtp={onSubmitOtp}
              label="Nhập mã từ ứng dụng xác thực"
              confirmLabel="Hoàn tất cài đặt"
            />
          </Box>
        )}
      </Box>
      {!qrResetUrl && (
        <DialogActions sx={{ px: 0, pt: 1 }}>
          <ButtonCustom onClick={handleSubmitNoApp} disabled={resetBusy}>
            {resetBusy ? "Đang tải..." : "Lấy mã QR"}
          </ButtonCustom>
        </DialogActions>
      )}
    </>
  );
});

export default TabNoApp;
